import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, BuildingOfficeIcon, CogIcon } from '@heroicons/react/24/outline';
import { Site, NewSite, UpdateSite, Turbine, NewTurbine, UpdateTurbine } from '@/types';
import { sitesApi } from '@/services/api/sites';
import { turbinesApi } from '@/services/api/turbines';
import { useFleetAndSiteStats, usePermissions } from '@/hooks';
import {
  PageToolbar,
  ToolbarIcons,
  ButtonConfig,
  ListCard,
  ConfigCard,
  ModalCard,
  StatsCard,
  type ListItem,
  type ConfigField,
} from '../../components/ui';

interface SiteModalData {
  site_id?: string;
  site_name: string;
  aihub_name: string;
  fieldpro_name: string;
  fm_name: string;
}

interface TurbineModalData {
  turbine_id?: string;
  site_id: string;
  turbine_name: string;
  cod_date: string;
  model_id: string;
  manufacturer_name?: string;
  model_name?: string;
}

const Sites: React.FC = () => {
  // State management
  const [currentSitePage, setCurrentSitePage] = useState(1);
  const [currentTurbinePage, setCurrentTurbinePage] = useState(1);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);

  // Modal states
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [isTurbineModalOpen, setIsTurbineModalOpen] = useState(false);
  const [siteModalData, setSiteModalData] = useState<SiteModalData>({
    site_name: '',
    aihub_name: '',
    fieldpro_name: '',
    fm_name: '',
  });
  const [turbineModalData, setTurbineModalData] = useState<TurbineModalData>({
    site_id: '',
    turbine_name: '',
    cod_date: '',
    model_id: '',
    manufacturer_name: '',
    model_name: '',
  });

  const queryClient = useQueryClient();

  // Permission checks
  const permissions = usePermissions();

  // Use reusable fleet and site statistics hooks
  const { fleet, selectedSite: selectedSiteStats } = useFleetAndSiteStats(selectedSite);

  // API Queries for pagination (sites)
  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ['sites', currentSitePage],
    queryFn: () => sitesApi.getSites({ page: currentSitePage, size: 10 }),
    staleTime: 5 * 60 * 1000,
  });

  // API Query for paginated turbines (for the list display)
  const { data: turbinesData, isLoading: turbinesLoading } = useQuery({
    queryKey: ['turbines', selectedSite, currentTurbinePage],
    queryFn: () =>
      turbinesApi.getTurbines({
        page: currentTurbinePage,
        size: 20,
        site_id: selectedSite || undefined,
      }),
    enabled: !!selectedSite,
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const createSiteMutation = useMutation({
    mutationFn: sitesApi.createSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      closeSiteModal();
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: sitesApi.updateSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      closeSiteModal();
    },
  });

  const createTurbineMutation = useMutation({
    mutationFn: turbinesApi.createTurbine,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      closeTurbineModal();
    },
  });

  const updateTurbineMutation = useMutation({
    mutationFn: turbinesApi.updateTurbine,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      closeTurbineModal();
    },
  });

  // Site Modal Handlers
  const openNewSiteModal = () => {
    setSiteModalData({
      site_name: '',
      aihub_name: '',
      fieldpro_name: '',
      fm_name: '',
    });
    setIsSiteModalOpen(true);
  };

  const openEditSiteModal = (site: Site) => {
    setSiteModalData({
      site_id: site.site_id,
      site_name: site.site_name,
      aihub_name: site.aihub_name,
      fieldpro_name: site.fieldpro_name,
      fm_name: site.fm_name,
    });
    setIsSiteModalOpen(true);
  };

  const closeSiteModal = () => {
    setIsSiteModalOpen(false);
    setSiteModalData({
      site_name: '',
      aihub_name: '',
      fieldpro_name: '',
      fm_name: '',
    });
  };

  const submitSiteModal = async () => {
    if (siteModalData.site_id) {
      const updateData: UpdateSite = {
        site_id: siteModalData.site_id,
        site_name: siteModalData.site_name,
        aihub_name: siteModalData.aihub_name,
        fieldpro_name: siteModalData.fieldpro_name,
        fm_name: siteModalData.fm_name,
      };
      updateSiteMutation.mutate(updateData);
    } else {
      const newData: NewSite = {
        site_name: siteModalData.site_name,
        aihub_name: siteModalData.aihub_name,
        fieldpro_name: siteModalData.fieldpro_name,
        fm_name: siteModalData.fm_name,
      };
      createSiteMutation.mutate(newData);
    }
  };

  // Turbine Modal Handlers
  const openNewTurbineModal = () => {
    if (!selectedSite) return;

    setTurbineModalData({
      site_id: selectedSite,
      turbine_name: '',
      cod_date: '',
      model_id: '',
      manufacturer_name: '',
      model_name: '',
    });
    setIsTurbineModalOpen(true);
  };

  const openEditTurbineModal = (turbine: Turbine) => {
    setTurbineModalData({
      turbine_id: turbine.turbine_id,
      site_id: turbine.site_id,
      turbine_name: turbine.turbine_name,
      cod_date: turbine.cod_date.split('T')[0],
      model_id: turbine.model_id,
      manufacturer_name: turbine.manufacturer_name,
      model_name: turbine.model_name,
    });
    setIsTurbineModalOpen(true);
  };

  const closeTurbineModal = () => {
    setIsTurbineModalOpen(false);
    setTurbineModalData({
      site_id: '',
      turbine_name: '',
      cod_date: '',
      model_id: '',
      manufacturer_name: '',
      model_name: '',
    });
  };

  const submitTurbineModal = async () => {
    if (turbineModalData.turbine_id) {
      const updateData: UpdateTurbine = {
        turbine_id: turbineModalData.turbine_id,
        turbine_name: turbineModalData.turbine_name,
        cod_date: turbineModalData.cod_date,
        model_id: turbineModalData.model_id,
      };
      updateTurbineMutation.mutate(updateData);
    } else {
      const newData: NewTurbine = {
        site_id: turbineModalData.site_id,
        turbine_name: turbineModalData.turbine_name,
        cod_date: turbineModalData.cod_date,
        model_id: turbineModalData.model_id,
      };
      createTurbineMutation.mutate(newData);
    }
  };

  // Pagination handlers
  const nextSitePage = () => {
    if (sitesData?.has_more) {
      setCurrentSitePage((prev) => prev + 1);
    }
  };

  const prevSitePage = () => {
    if (currentSitePage > 1) {
      setCurrentSitePage((prev) => prev - 1);
    }
  };

  const nextTurbinePage = () => {
    if (turbinesData?.has_more) {
      setCurrentTurbinePage((prev) => prev + 1);
    }
  };

  const prevTurbinePage = () => {
    if (currentTurbinePage > 1) {
      setCurrentTurbinePage((prev) => prev - 1);
    }
  };

  // Handle site selection
  const handleSiteSelect = (item: ListItem) => {
    setSelectedSite(item.id);
    setCurrentTurbinePage(1);
  };

  // Convert sites to ListItem format
  const siteListItems: ListItem[] =
    sitesData?.sites.map((site) => ({
      id: site.site_id,
      primary: site.site_name,
      secondary: site.aihub_name,
      icon: <BuildingOfficeIcon />,
      selected: selectedSite === site.site_id,
      actions: permissions.canUpdateSite() ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openEditSiteModal(site);
          }}
          className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
          title="Edit Site"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      ) : undefined,
    })) || [];

  // Convert turbines to ListItem format
  const turbineListItems: ListItem[] =
    turbinesData?.turbines.map((turbine) => ({
      id: turbine.turbine_id,
      primary: turbine.turbine_name,
      secondary: `${turbine.manufacturer_name} ${turbine.model_name}`,
      tertiary: `COD: ${new Date(turbine.cod_date).toLocaleDateString()}`,
      icon: <CogIcon />,
      actions: permissions.canUpdateTurbine() ? (
        <button
          onClick={() => openEditTurbineModal(turbine)}
          className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
          title="Edit Turbine"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      ) : undefined,
    })) || [];

  // Site configuration fields
  const siteConfigFields: ConfigField[] = [
    {
      id: 'site_name',
      label: 'Site Name',
      type: 'text',
      value: siteModalData.site_name,
      placeholder: 'Enter the site name',
      required: true,
    },
    {
      id: 'aihub_name',
      label: 'AI Hub Name',
      type: 'text',
      value: siteModalData.aihub_name,
      placeholder: 'Enter the name in AI Hub',
    },
    {
      id: 'fieldpro_name',
      label: 'FieldPRO Name',
      type: 'text',
      value: siteModalData.fieldpro_name,
      placeholder: 'Enter the name in FieldPRO',
    },
    {
      id: 'fm_name',
      label: 'fleetMONITOR Name',
      type: 'text',
      value: siteModalData.fm_name,
      placeholder: 'Enter the name in fleetMONITOR',
    },
  ];

  // Turbine configuration fields
  const turbineConfigFields: ConfigField[] = [
    {
      id: 'turbine_name',
      label: 'Turbine Name',
      type: 'text',
      value: turbineModalData.turbine_name,
      placeholder: 'e.g., CC2-G01',
      required: true,
    },
    {
      id: 'cod_date',
      label: 'Commercial Operation Date',
      type: 'date',
      value: turbineModalData.cod_date,
      required: true,
    },
    {
      id: 'model_display',
      label: 'Model',
      type: 'text',
      value:
        `${turbineModalData.manufacturer_name || ''} ${turbineModalData.model_name || ''}`.trim(),
      disabled: true,
      placeholder: 'Model information will be populated automatically',
    },
  ];

  // Handle config field changes
  const handleSiteFieldChange = (fieldId: string, value: string | number) => {
    setSiteModalData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleTurbineFieldChange = (fieldId: string, value: string | number) => {
    setTurbineModalData((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Summary statistics using reusable hooks
  const summaryStats = React.useMemo(() => {
    return {
      totalSites: fleet.siteCount,
      fleetTurbines: fleet.turbineCount,
      selectedSite: selectedSiteStats.name || (selectedSite ? 'Unknown' : 'None'),
      selectedSiteTurbines: selectedSiteStats.turbineCount,
    };
  }, [fleet, selectedSiteStats, selectedSite]);

  // Configure toolbar buttons (minimal - no add button)
  const toolbarButtons: ButtonConfig[] = [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Toolbar */}
      <PageToolbar
        title="Sites Management"
        icon={ToolbarIcons.AddSite}
        buttons={toolbarButtons}
      />

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatsCard
            title="Total Sites"
            value={summaryStats.totalSites}
            description="Wind farm locations"
            color="purple"
            loading={fleet.isLoading}
          />

          <StatsCard
            title="Fleet Turbines"
            value={summaryStats.fleetTurbines}
            description="Total across all sites"
            color="fleet-monitor-blue"
            loading={fleet.isLoading}
          />

          <StatsCard
            title="Selected Site"
            value={summaryStats.selectedSite}
            description="Currently viewing"
            color="bright-blue"
          />

          <StatsCard
            title={selectedSite ? 'Site Turbines' : 'Select Site'}
            value={selectedSite ? summaryStats.selectedSiteTurbines : '—'}
            description={selectedSite ? 'At selected site' : 'Choose a site to view count'}
            color="green"
            loading={selectedSiteStats.isLoading}
          />
        </div>

        {/* Master-Detail Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sites Panel (Left) */}
          <ListCard
            title="Sites"
            subtitle={sitesLoading ? 'Loading...' : `${sitesData?.total_count || 0} sites total`}
            items={siteListItems}
            onItemClick={handleSiteSelect}
            headerActions={
              permissions.canCreateSite() ? (
                <button
                  onClick={openNewSiteModal}
                  className="px-3 py-1.5 bg-onyx-purple text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-dark-blue transition-colors text-sm"
                >
                  <PlusIcon className="h-4 w-4 inline mr-1" />
                  Add Site
                </button>
              ) : undefined
            }
            footerActions={
              sitesData && sitesData.total_pages > 1 ? (
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm text-gray-700 onyx-body">
                    Page {currentSitePage} of {sitesData.total_pages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={prevSitePage}
                      disabled={currentSitePage <= 1}
                      className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={nextSitePage}
                      disabled={!sitesData.has_more}
                      className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : undefined
            }
            loading={sitesLoading}
            emptyState={{
              icon: <BuildingOfficeIcon />,
              title: 'No sites found',
              description: 'Get started by adding your first wind farm site',
              action: permissions.canCreateSite() ? (
                <button
                  onClick={openNewSiteModal}
                  className="px-4 py-2 bg-onyx-purple text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-dark-blue transition-colors"
                >
                  <PlusIcon className="h-4 w-4 inline mr-2" />
                  Add First Site
                </button>
              ) : undefined,
            }}
          />

          {/* Turbines Panel (Right) */}
          <ListCard
            title={selectedSite ? 'Turbines' : 'Select a Site'}
            subtitle={
              !selectedSite
                ? 'Choose a site from the left panel to view its turbines'
                : turbinesLoading
                  ? 'Loading turbines...'
                  : `${turbinesData?.total_count || 0} turbines at selected site`
            }
            items={turbineListItems}
            headerActions={
              selectedSite && permissions.canCreateTurbine() ? (
                <button
                  onClick={openNewTurbineModal}
                  className="px-3 py-1.5 bg-onyx-fleet-monitor-blue text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-bright-blue transition-colors text-sm"
                >
                  <PlusIcon className="h-4 w-4 inline mr-1" />
                  Add Turbine
                </button>
              ) : undefined
            }
            footerActions={
              selectedSite && turbinesData && turbinesData.total_pages > 1 ? (
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm text-gray-700 onyx-body">
                    Page {currentTurbinePage} of {turbinesData.total_pages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={prevTurbinePage}
                      disabled={currentTurbinePage <= 1}
                      className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={nextTurbinePage}
                      disabled={!turbinesData.has_more}
                      className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : undefined
            }
            loading={selectedSite ? turbinesLoading : false}
            emptyState={
              !selectedSite
                ? {
                    icon: <BuildingOfficeIcon />,
                    title: 'Select a Site',
                    description: 'Choose a site from the left panel to view its turbines',
                  }
                : {
                    icon: <CogIcon />,
                    title: 'No turbines found',
                    description: 'Add the first turbine to this site',
                    action: permissions.canCreateTurbine() ? (
                      <button
                        onClick={openNewTurbineModal}
                        className="px-4 py-2 bg-onyx-fleet-monitor-blue text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-bright-blue transition-colors"
                      >
                        <PlusIcon className="h-4 w-4 inline mr-2" />
                        Add First Turbine
                      </button>
                    ) : undefined,
                  }
            }
          />
        </div>

        {/* Site Configuration Modal */}
        <ModalCard
          isOpen={isSiteModalOpen}
          onClose={closeSiteModal}
          title={siteModalData.site_id ? 'Edit Site Details' : 'Add New Site'}
          subtitle="Configure wind farm site information and system names"
          size="md"
        >
          <ConfigCard
            fields={siteConfigFields}
            onFieldChange={handleSiteFieldChange}
            onSubmit={submitSiteModal}
            onCancel={closeSiteModal}
            submitLabel={siteModalData.site_id ? 'Update Site' : 'Create Site'}
            isSubmitting={createSiteMutation.isPending || updateSiteMutation.isPending}
          />
        </ModalCard>

        {/* Turbine Configuration Modal */}
        <ModalCard
          isOpen={isTurbineModalOpen}
          onClose={closeTurbineModal}
          title={turbineModalData.turbine_id ? 'Edit Turbine Details' : 'Add New Turbine'}
          subtitle="Configure turbine asset information and operational dates"
          size="md"
        >
          <ConfigCard
            fields={turbineConfigFields}
            onFieldChange={handleTurbineFieldChange}
            onSubmit={submitTurbineModal}
            onCancel={closeTurbineModal}
            submitLabel={turbineModalData.turbine_id ? 'Update Turbine' : 'Create Turbine'}
            isSubmitting={createTurbineMutation.isPending || updateTurbineMutation.isPending}
          />
        </ModalCard>
      </div>
    </div>
  );
};

export default Sites;
